// ============================================
// Tipos para Calendario de Mantenimiento de Plotters
// ============================================

export type PlotterMaintenanceTaskType =
  | 'routine_cleaning'
  | 'blade_change'
  | 'calibration'
  | 'lubrication'
  | 'deep_cleaning'
  | 'inspection'
  | 'other';

export type PlotterMaintenanceStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'skipped'
  | 'overdue';

export type RecurrencePattern =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly';

export interface ConsumableUsed {
  name: string;
  quantity: number;
  cost?: number;
}

export interface ChecklistItem {
  task: string;
  completed: boolean;
}

export interface PlotterMaintenance {
  id: string;
  machine_id: string;
  task_type: PlotterMaintenanceTaskType;
  title: string;
  description?: string;
  scheduled_date: string;
  completed_date?: string;
  estimated_duration_minutes: number;
  actual_duration_minutes?: number;
  status: PlotterMaintenanceStatus;
  is_recurring: boolean;
  recurrence_pattern?: RecurrencePattern;
  recurrence_interval: number;
  parent_task_id?: string;
  operating_hours_at_service?: number;
  parts_replaced?: string[];
  consumables_used: ConsumableUsed[];
  labor_cost?: number;
  parts_cost?: number;
  total_cost?: number;
  assigned_to?: string;
  performed_by?: string;
  notes?: string;
  checklist: ChecklistItem[];
  photos?: string[];
  priority: number;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PlotterMaintenanceWithMachine extends PlotterMaintenance {
  machine?: {
    id: string;
    name: string;
    code?: string;
    model?: string;
    type?: string;
  };
  assigned_to_name?: string;
  performed_by_name?: string;
}

// Configuración de tipos de tarea para UI
export const TASK_TYPE_CONFIG: Record<PlotterMaintenanceTaskType, {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  defaultDuration: number;
  defaultChecklist: string[];
}> = {
  routine_cleaning: {
    label: 'Limpieza Rutinaria',
    icon: 'Sparkles',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500/10',
    defaultDuration: 15,
    defaultChecklist: [
      'Limpiar superficie de corte',
      'Aspirar residuos de material',
      'Limpiar cabezal de corte',
      'Verificar estado de cuchilla',
    ],
  },
  blade_change: {
    label: 'Cambio de Cuchillas',
    icon: 'Scissors',
    color: 'text-orange-600',
    bgColor: 'bg-orange-500/10',
    defaultDuration: 45,
    defaultChecklist: [
      'Apagar y desconectar máquina',
      'Retirar cuchilla usada',
      'Instalar cuchilla nueva',
      'Calibrar profundidad de corte',
      'Realizar corte de prueba',
    ],
  },
  calibration: {
    label: 'Calibración',
    icon: 'Target',
    color: 'text-purple-600',
    bgColor: 'bg-purple-500/10',
    defaultDuration: 60,
    defaultChecklist: [
      'Verificar alineación de ejes',
      'Calibrar presión de corte',
      'Calibrar velocidad de corte',
      'Verificar precisión dimensional',
      'Documentar parámetros',
    ],
  },
  lubrication: {
    label: 'Lubricación',
    icon: 'Droplet',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500/10',
    defaultDuration: 20,
    defaultChecklist: [
      'Lubricar guías lineales',
      'Lubricar husillos',
      'Verificar nivel de lubricante',
      'Limpiar exceso de lubricante',
    ],
  },
  deep_cleaning: {
    label: 'Limpieza Profunda',
    icon: 'Brush',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-500/10',
    defaultDuration: 90,
    defaultChecklist: [
      'Desmontar cubiertas protectoras',
      'Limpiar interior de la máquina',
      'Limpiar sensores y detectores',
      'Limpiar sistema de ventilación',
      'Verificar conexiones eléctricas',
      'Remontar cubiertas',
    ],
  },
  inspection: {
    label: 'Inspección',
    icon: 'Search',
    color: 'text-green-600',
    bgColor: 'bg-green-500/10',
    defaultDuration: 30,
    defaultChecklist: [
      'Inspección visual general',
      'Verificar desgaste de componentes',
      'Comprobar funcionamiento de seguridades',
      'Revisar estado de cables y conexiones',
      'Documentar hallazgos',
    ],
  },
  other: {
    label: 'Otro',
    icon: 'Wrench',
    color: 'text-gray-600',
    bgColor: 'bg-gray-500/10',
    defaultDuration: 30,
    defaultChecklist: [],
  },
};

// Configuración de estados para UI
export const STATUS_CONFIG: Record<PlotterMaintenanceStatus, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  scheduled: {
    label: 'Programado',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
  },
  in_progress: {
    label: 'En Progreso',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
  },
  completed: {
    label: 'Completado',
    color: 'text-green-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
  },
  skipped: {
    label: 'Omitido',
    color: 'text-gray-600',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/30',
  },
  overdue: {
    label: 'Vencido',
    color: 'text-red-600',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
  },
};

// Configuración de patrones de recurrencia
export const RECURRENCE_CONFIG: Record<RecurrencePattern, {
  label: string;
  description: string;
}> = {
  daily: { label: 'Diario', description: 'Cada día' },
  weekly: { label: 'Semanal', description: 'Cada semana' },
  biweekly: { label: 'Quincenal', description: 'Cada 2 semanas' },
  monthly: { label: 'Mensual', description: 'Cada mes' },
  quarterly: { label: 'Trimestral', description: 'Cada 3 meses' },
  yearly: { label: 'Anual', description: 'Cada año' },
};
